import type { AutocompleteProps, TextFieldProps } from "@mui/material"
import { Autocomplete, TextField } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import { type ReactElement, forwardRef, useMemo, useState } from "react"

import { MENU_MAX_HEIGHT } from "@/constants/forms"
import type { Option } from "@/types/form-fields"

export type AerosAutocompleteProps<
  T extends string | number = string,
  FreeSolo extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<Option<T>, false, false, FreeSolo>,
  "onChange" | "renderInput" | "options" | "value" | "inputValue" | "onInputChange" | "multiple"
> & {
  options: Option<T>[]
  value?: T | null
  inputValue?: string
  label?: string
  placeholder?: string
  helperText?: string
  required?: boolean
  error?: boolean
  onChange?: (value: T | null) => void
  onInputChange?: (value: string) => void
  slotProps?: {
    textField?: TextFieldProps
    listbox?: React.HTMLAttributes<HTMLUListElement> & { ref?: React.Ref<HTMLUListElement> }
  }
  "data-testid"?: string
}

export const AerosAutocomplete = forwardRef(
  <T extends string | number = string>(
    {
      options,
      value,
      inputValue: controlledInputValue,
      label,
      placeholder,
      helperText,
      required,
      error,
      freeSolo = false,
      onChange,
      onInputChange,
      slotProps,
      "data-testid": testId,
      ...props
    }: AerosAutocompleteProps<T>,
    ref: React.Ref<HTMLDivElement>
  ): ReactElement => {
    const [internalInputValue, setInternalInputValue] = useState("")

    const matchedOption = useMemo(
      // eslint-disable-next-line eqeqeq
      () => (value == null ? null : (options.find((option) => option.value === value) ?? null)),
      [value, options]
    )

    const handleChange = (_event: unknown, selectedOption: Option<T> | null) => {
      onChange?.(selectedOption?.value ?? null)
    }

    const handleInputChange = (_event: unknown, newInputValue: string, reason: string) => {
      if (controlledInputValue === undefined) {
        setInternalInputValue(newInputValue)
      }

      // Only notify on actual user input or explicit clear — skip "reset" (triggered
      // by MUI after option selection) and "selectOption" (MUI sets the input to the
      // selected label, which would cause spurious re-queries on server-side filtering).
      if (reason !== "reset" && reason !== "selectOption") {
        onInputChange?.(newInputValue)
      }

      if (freeSolo) {
        onChange?.(newInputValue as T)
      }
    }

    return (
      <Autocomplete
        {...props}
        ref={ref}
        value={freeSolo ? null : matchedOption}
        inputValue={controlledInputValue ?? (freeSolo ? internalInputValue : undefined)}
        options={options}
        fullWidth
        freeSolo={freeSolo}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option
          }
          return option.label
        }}
        isOptionEqualToValue={(option, selectedOption) => {
          if (typeof option === "string" || typeof selectedOption === "string") {
            return option === selectedOption
          }
          return option.value === selectedOption.value
        }}
        getOptionDisabled={(option) => {
          if (typeof option === "string") {
            return false
          }
          return option.disabled ?? false
        }}
        popupIcon={<KeyboardArrowDownOutlined />}
        slotProps={{
          listbox: {
            ...slotProps?.listbox,
            style: { maxHeight: MENU_MAX_HEIGHT, ...slotProps?.listbox?.style },
          },
        }}
        renderOption={(optionProps, option) => {
          const key = typeof option === "string" ? option : option.value
          const display = typeof option === "string" ? option : (option.customLabel ?? option.label)
          return (
            <li {...optionProps} key={key}>
              {display}
            </li>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...slotProps?.textField}
            label={label}
            required={required}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            variant='outlined'
            data-testid={testId}
            slotProps={{
              input: {
                ...params.InputProps,
                ...slotProps?.textField?.slotProps?.input,
              },
              htmlInput: params.inputProps,
            }}
          />
        )}
      />
    )
  }
) as <T extends string | number = string, FreeSolo extends boolean | undefined = false>(
  props: AerosAutocompleteProps<T, FreeSolo> & { ref?: React.Ref<unknown> }
) => ReactElement
